/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { FormControlLabel, Grid, Radio, RadioGroup } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { selectVisualFilters } from 'modules/dashboard/selectors';
import { useEffect, useState } from 'react';
import {
  selectFavoriteScenarios,
  selectScenarios,
  selectSearchData,
  selectSelectedScenario,
} from '../../selectors';
import { searchActions } from '../../slice';
import { getSubPaceFilter } from '../../../inner-filter/functions';

/**
 * SearchBookingPaceHorizonSelector
 *
 * Renders a radio-based selector for booking pace horizon values.
 * Updates the selected filter inside the relevant scenario + visual
 *
 * @component
 * @param {Object} props.filterData - Filter info for the selected scenario and visual.
 * @param {Function} props.handleClose - Callback to close the selector after selection.
 */
const SearchBookingPaceHorizonSelector = ({ filterData, found, handleClose }) => {
  const visualFilters = useSelector(selectVisualFilters);
  const scenarios = useSelector(selectScenarios);
  const searchScenarios = useSelector(selectSearchData);
  const favoriteScenarios = useSelector(selectFavoriteScenarios);
  const selectedScenario = useSelector(selectSelectedScenario);
  const [paceList, setPaceList] = useState([]);
  //
  const dispatch = useDispatch();
  //
  useEffect(() => {
    // Update pace variable filter list
    const subPaceFilter = getSubPaceFilter(visualFilters);
    if (subPaceFilter) {
      setPaceList(subPaceFilter?.map((pace) => pace.label?.[0]));
    }
  }, [visualFilters]);
  //
  const handleChange = (event, value) => {
    if (scenarios?.results.length) {
      const updatedScenarios = scenarios?.results.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        //
        return {
          ...scenario,
          description: scenario.description.replace(found?.keyword, value),
          visuals: scenario.visuals.map((visual) => ({
            ...visual,
            filters: Object.fromEntries(
              Object.entries(visual.filters).map(([filterName, filter]) => {
                if (filter?.keyword === found?.keyword) {
                  return [
                    filterName,
                    {
                      ...filter,
                      keyword: value,
                      value,
                    },
                  ];
                }
                return [filterName, filter];
              })
            ),
          })),
        };
      });
      //
      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setScenarios({ results: updatedScenarios }));
    }
    //
    if (favoriteScenarios.length) {
      const updatedScenarios = favoriteScenarios.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        //
        return {
          ...scenario,
          description: scenario.description.replace(found?.keyword, value),
          visuals: scenario.visuals.map((visual) => ({
            ...visual,
            filters: Object.fromEntries(
              Object.entries(visual.filters).map(([filterName, filter]) => {
                if (filter?.keyword === found?.keyword) {
                  return [
                    filterName,
                    {
                      ...filter,
                      keyword: value,
                      value,
                    },
                  ];
                }
                return [filterName, filter];
              })
            ),
          })),
        };
      });
      //
      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setFavoriteScenario(updatedScenarios));
    }
    //
    if (searchScenarios.length) {
      const updatedScenarios = searchScenarios.map((scenario) => {
        if (scenario.id !== filterData?.scenarioId) return scenario;
        //
        return {
          ...scenario,
          description: scenario.description.replace(found?.keyword, value),
          visuals: scenario.visuals.map((visual) => ({
            ...visual,
            filters: Object.fromEntries(
              Object.entries(visual.filters).map(([filterName, filter]) => {
                if (filter?.keyword === found?.keyword) {
                  return [
                    filterName,
                    {
                      ...filter,
                      keyword: value,
                      value,
                    },
                  ];
                }
                return [filterName, filter];
              })
            ),
          })),
        };
      });
      if (selectedScenario) {
        const updatedScenario = updatedScenarios?.find((e) => e.id === filterData?.scenarioId);
        dispatch(searchActions.setSelectedScenario(updatedScenario));
      }
      dispatch(searchActions.setSearchScenario(updatedScenarios));
    }
    handleClose();
  };
  //
  const selectedDay = paceList?.find((pace) => pace === found?.value);
  //
  return (
    <Grid
      container
      flexDirection="row"
      justifyContent="flex-end"
      alignItems="center"
      wrap="nowrap"
      bgcolor="#ffffff"
    >
      <RadioGroup value={selectedDay ?? null} onChange={handleChange}>
        {paceList
          ?.sort((a, b) => Number(a.split(' ')[0]) - Number(b.split(' ')[0]))
          .map((label) => (
            <FormControlLabel
              key={label}
              value={label}
              control={<Radio />}
              label={label}
              sx={{ ml: 2, my: -1 }}
            />
          ))}
      </RadioGroup>
    </Grid>
  );
};
//
export default SearchBookingPaceHorizonSelector;
